import * as React from "react";
import { useRef } from "react";
import { styled, css } from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { PAGE_PRINT_MARGIN_PX, PAGE_SIZES_MAP, DEFAULT_SIZE, DEFAULT_ORIENTATION } from "report/Constants";
import useElementDimensions from "hooks/useElementDimensions";
import usePaperDimensions from "report/report-creation/common/usePaperDimensions";
export const PREVIEW_PADDING = 40;
const Container = styled.div(
  ({ $orientation }) => css`
    width: ${$orientation === "portrait" ? 60 : 85}%;
    margin: ${PREVIEW_PADDING}px 0;
    height: fit-content;
  `
);
const FixedDimensions = styled.div(
  ({ $width, $height, $layoutMultiplier }) => css`
    overflow: hidden;
    position: relative;
    width: ${$width}px;
    height: ${$height + PAGE_PRINT_MARGIN_PX * $layoutMultiplier * 2}px;
  `
);
const Page = styled.div(
  ({ $pageScaleFactor, $width, $minHeight, $layoutMultiplier }) => css`
    // The top padding is calculated differently in the actual PDF, because the header is not part of the page content.
    padding: ${PAGE_PRINT_MARGIN_PX}px ${PAGE_PRINT_MARGIN_PX * $layoutMultiplier}px;
    width: ${$width}px;
    transform: scale(${$pageScaleFactor});
    transform-origin: top left;
    min-height: ${$minHeight}px;
    background-color: white;
    border-radius: 2px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    color: black;
  `
);
const Header = styled.div(
  ({ $layoutMultiplier }) => css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${15 * $layoutMultiplier}px;
  `
);
const Footer = styled(Header)(
  ({ $layoutMultiplier }) => css`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-family: Arial;
    gap: ${15 * $layoutMultiplier}px;
    background-color: rgba(255, 255, 255, 0.9);
  `
);
const Logo = styled.div(
  ({ $layoutMultiplier }) => css`
    min-width: ${130 * $layoutMultiplier}px;

    img {
      max-height: ${130 * $layoutMultiplier}px;
      max-width: ${130 * $layoutMultiplier}px;
    }
  `
);
const PageNumbers = styled.div(
  ({ $layoutMultiplier }) => css`
    min-width: ${130 * $layoutMultiplier}px;
  `
);
const PDFPage = ({ children, pageSize: pageSizeProp, pageOrientation: pageOrientationProp, report }) => {
  var _a, _b, _c, _d, _e;
  const { t } = useTranslation("enterprise_report");
  const pageSize = pageSizeProp != null ? pageSizeProp : DEFAULT_SIZE;
  const pageOrientation = pageOrientationProp != null ? pageOrientationProp : DEFAULT_ORIENTATION;
  const containerRef = useRef(null);
  const pageRef = useRef(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const paperSize = usePaperDimensions(pageSize, pageOrientation);
  const scaledPageDimensions = useElementDimensions(pageRef);
  const pageScaleFactor = containerWidth / paperSize.width;
  const previewLayoutMultiplier = PAGE_SIZES_MAP[pageSize].previewLayoutMultiplier[pageOrientation];
  return /* @__PURE__ */ React.createElement(Container, { ref: containerRef, $orientation: pageOrientation }, /* @__PURE__ */ React.createElement(
    FixedDimensions,
    {
      $width: paperSize.width * pageScaleFactor,
      $height: scaledPageDimensions.height * pageScaleFactor,
      $layoutMultiplier: previewLayoutMultiplier
    },
    /* @__PURE__ */ React.createElement(
      Page,
      {
        $pageScaleFactor: pageScaleFactor,
        $width: paperSize.width,
        $minHeight: paperSize.height,
        $layoutMultiplier: previewLayoutMultiplier,
        ref: pageRef
      },
      ((_a = report.layout) == null ? void 0 : _a.printHeader) && /* @__PURE__ */ React.createElement(Header, { $layoutMultiplier: previewLayoutMultiplier }, report.logo && /* @__PURE__ */ React.createElement(Logo, { $layoutMultiplier: previewLayoutMultiplier }, /* @__PURE__ */ React.createElement("img", { src: report.logo, alt: t("pdf_page.logo_alt") })), (_b = report.layout) == null ? void 0 : _b.header),
      children({ contentWidth: paperSize.width - PAGE_PRINT_MARGIN_PX * 2, pageScaleFactor }),
      ((_c = report.layout) == null ? void 0 : _c.printFooter) && /* @__PURE__ */ React.createElement(Footer, { $layoutMultiplier: previewLayoutMultiplier }, ((_d = report.layout) == null ? void 0 : _d.printPageNumbers) && /* @__PURE__ */ React.createElement(PageNumbers, { $layoutMultiplier: previewLayoutMultiplier }, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "pdf_page.page_numbers", components: { i: /* @__PURE__ */ React.createElement("i", null) } })), (_e = report.layout) == null ? void 0 : _e.footer)
    )
  ));
};
export default PDFPage;
